import { useCallback, useRef } from 'react';

/**
 * Custom hook to forward refs from library components while maintaining internal ref access
 * This is commonly needed when using Material-UI's autocomplete or other library components
 * that provide their own ref which needs to be forwarded to the DOM element
 */
export const useForwardedRef = <T extends HTMLElement>(libraryRef?: React.Ref<T>) => {
  const internalRef = useRef<T | null>(null);

  const forwardedRef = useCallback(
    (el: T | null) => {
      internalRef.current = el;
      if (libraryRef) {
        if (typeof libraryRef === 'function') {
          libraryRef(el);
        } else if (libraryRef && 'current' in libraryRef) {
          (libraryRef as React.MutableRefObject<T | null>).current = el;
        }
      }
    },
    [libraryRef],
  );

  return { internalRef, forwardedRef };
};
